import {
  CellClassParams,
  GridApi,
  GridReadyEvent,
  NewValueParams,
  ValueGetterParams,
} from 'ag-grid-community';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';
import { AgGridColumn } from 'ag-grid-react';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { TemplateAssigneePicker } from 'components/molecules/assignee-picker/template-assignee-picker';
import { suppressEnterKey } from 'components/organisms/tasks/utils';
import {
  ProjectTemplateData,
  useTransformTabularDataForAgGrid,
} from 'data/templates/projects';
import {
  updatePhaseInProjectTemplate,
  updateTaskInProjectTemplate,
} from 'data/templates/projects/actions';
import { useTranslate } from 'hooks/use-translation';
import { useActiveRecord } from '../../commons';
import { CustomNameRenderer } from '../custom-name-renderer';
import { NameEditor } from '../status-name-cell';
import { CellRendererParams, ValueFormatterParams } from '../types';
import { editable } from './editable';
import { AssigneeEditor, NumberEditor } from './editors';
import { GridContainer, StyledGrid } from './styled';
import { useOnCellClicked } from './use-on-cell-clicked';

const TemplateAssigneeRenderer: FC<CellRendererParams> = (props) => {
  const { data } = props;
  const { type } = data;

  if (!data.title || type !== 'TASK') {
    return null;
  }

  return <TemplateAssigneePicker {...props} readOnly isMini />;
};

interface Props {
  gridApi: GridApi | null;
  onGridReady: (event: GridReadyEvent) => void;
}

const cellClass = (params: CellClassParams) => {
  const isEditable = editable(params);

  return !isEditable ? 'not-editable' : '';
};

export const Grid: FC<Props> = ({ gridApi, onGridReady }) => {
  const dispatch = useDispatch();
  const translate = useTranslate();
  const activeRecord = useActiveRecord();

  const rowData = useTransformTabularDataForAgGrid();
  const onCellClicked = useOnCellClicked();

  const update = useCallback(
    ({ duration = '0', id, startDay = '0', type }) => {
      const durationParsed =
        parseInt(duration, 10) < 1 ? 1 : parseInt(duration, 10);
      const startDayParsed =
        parseInt(startDay, 10) < 1 ? 1 : parseInt(startDay, 10);

      if (type === 'PROJECT_PHASE') {
        dispatch(
          updatePhaseInProjectTemplate({
            projectPhaseId: id,
            payload: {
              duration: durationParsed,
              startDay: startDayParsed,
            },
          }),
        );
      } else {
        dispatch(
          updateTaskInProjectTemplate({
            taskId: id,
            payload: {
              duration: durationParsed,
              startDay: startDayParsed,
            },
          }),
        );
      }
    },
    [dispatch],
  );

  const onCellValueChanged = useCallback(
    (params: NewValueParams) => {
      const { newValue, colDef } = params;
      const row = params.data as ProjectTemplateData;
      const { field } = colDef;

      if (!field) {
        return;
      }
      const payload = {
        ...row,
        [field]:
          (field === 'startDay' && !newValue) ||
          (field === 'duration' && !newValue)
            ? 1
            : newValue,
      };

      update(payload);
    },
    [update],
  );

  const valueFormatter = useCallback((params: ValueFormatterParams) => {
    return params.data.data.isNew ? '' : `${params.value}d`;
  }, []);

  const rowClassRules = useMemo(() => {
    return {
      'is-private': (params) => params.data.private,
    };
  }, []);

  useEffect(() => {
    if (!gridApi || !activeRecord) {
      return;
    }
    const node = gridApi.getRowNode(activeRecord.id.toString());

    if (node != null) {
      node.setSelected(true);
      gridApi.ensureIndexVisible(node.rowIndex);
    }
  }, [gridApi, activeRecord]);

  return (
    <GridContainer className="ag-theme-alpine">
      <StyledGrid
        rowData={rowData}
        immutableData
        defaultColDef={{
          filter: false,
          sortable: false,
          resizable: false,
          wrapText: true,
          suppressMovable: true,
          suppressMenu: true,
        }}
        suppressContextMenu
        autoGroupColumnDef={{
          headerName: 'Name',
          pinned: true,
          minWidth: 326,
          field: 'title',
          editable: (params) => params.data.data.isNew,
          sortable: false,
          valueGetter: (param: ValueGetterParams) => param.data.title ?? '',
          autoHeight: true,
          wrapText: true,
          cellEditor: 'NameEditor',
          suppressKeyboardEvent: suppressEnterKey,
          cellRendererParams: {
            checkbox: false,
            suppressCount: true,
            suppressDoubleClickExpand: true,
            suppressEnterExpand: true,
            innerRenderer: 'CustomNameRenderer',
          },
        }}
        suppressRowClickSelection
        frameworkComponents={{
          CustomNameRenderer,
          NameEditor,
          NumberEditor,
          TemplateAssigneeEditor: AssigneeEditor,
          TemplateAssigneeRenderer,
        }}
        treeData
        animateRows
        gridOptions={{
          enterMovesDownAfterEdit: true,
          enterMovesDown: false,
        }}
        groupDefaultExpanded={-1}
        getDataPath={(d: ProjectTemplateData) => d.hierarchy ?? []}
        getRowNodeId={(d: ProjectTemplateData) => d.id.toString()}
        onGridReady={onGridReady}
        rowClassRules={rowClassRules}
        headerHeight={32}
        onCellClicked={onCellClicked}
      >
        <AgGridColumn
          editable={editable}
          singleClickEdit
          sortable={false}
          field="startDay"
          minWidth={45}
          width={78}
          headerName={translate('templates.starts_on')}
          valueFormatter={valueFormatter}
          onCellValueChanged={onCellValueChanged}
          cellEditor="NumberEditor"
          cellClass={cellClass}
        />
        <AgGridColumn
          editable={editable}
          singleClickEdit
          sortable={false}
          field="duration"
          minWidth={40}
          width={83}
          headerName={translate('templates.duration')}
          valueFormatter={valueFormatter}
          onCellValueChanged={onCellValueChanged}
          cellEditor="NumberEditor"
          cellClass={cellClass}
        />
        <AgGridColumn
          editable={({ data }) => data.title && data.type === 'TASK'}
          singleClickEdit
          sortable={false}
          field="assignee"
          minWidth={55}
          width={120}
          headerName={translate('templates.assignee')}
          cellRenderer="TemplateAssigneeRenderer"
          cellEditor="TemplateAssigneeEditor"
          onCellValueChanged={onCellValueChanged}
          cellClass="assignee"
        />
      </StyledGrid>
    </GridContainer>
  );
};
